import dayjs from 'dayjs';
import type { Color, CellCoords } from './types';
import { parseColor, colorToString, mixColors } from './utils';
import { DataManager } from './DataManager';

export class GridDrawer {
  private startDate!: dayjs.Dayjs;
  private nowDate!: dayjs.Dayjs;
  private totalCells!: number;
  private columnCount!: number;
  private rowCount!: number;
  private cellSize!: number;
  private padLeft!: number;

  private backgroundColor: Color = parseColor('#fff');
  private defaultColor: Color = parseColor('#808080');
  private highlightColor: Color = parseColor('#ff0');

  private lastMouseX: number = -1;
  private lastMouseY: number = -1;
  private selectionStartIndex: number = -1;
  private highlightedCellIndex: number = -1;

  constructor(
    private canvas: HTMLCanvasElement,
    private ctx: CanvasRenderingContext2D,
    private tooltip: HTMLElement,
    private dataManager: DataManager
  ) {}

  // full redraw
  drawGrid(): void {
    // Retrieve stored data
    const { birthDate, totalDays } = this.dataManager.getSelectedCalendarInfo();
    this.startDate = birthDate;
    this.nowDate = dayjs();

    // Calculate total cells (days)
    this.totalCells = totalDays

    const W = this.canvas.width;
    const H = this.canvas.height;

    // Compute max cell size
    let cellSize: number;
    for (let s = 1; ; s++) {
      const cols = Math.floor(W / s);
      const rows = Math.floor(H / s);
      if (cols * rows < totalDays) {
        break;
      }
      cellSize = s;
    }
    this.cellSize = cellSize!;

    // Padding / centering
    let columnCount = Math.floor(W / this.cellSize);
    const rowCount = this.rowCount = Math.floor(H / this.cellSize);
    while (columnCount > 0 && Math.ceil(totalDays / (columnCount - 1)) < rowCount) {
      columnCount--;
    }
    this.columnCount = columnCount;
    const usedWidth = columnCount * this.cellSize;
    this.padLeft = Math.floor((W - usedWidth) / 2);

    this.ctx.fillStyle = colorToString(this.backgroundColor);
    this.ctx.fillRect(0, 0, W, H);

    const selectedCellIndex = this.highlightedCellIndex;

    for (let row = 0, cellIndex = 0; row < rowCount; row++) {
      for (let col = 0; col < columnCount; col++, cellIndex++) {
        this.drawDayCell(row, col, cellIndex == selectedCellIndex);
      }
    }

    this.updateTooltip(selectedCellIndex);
  }

  mouseToCellCoords(mouseX: number, mouseY: number): CellCoords {
    return {
      row: Math.floor((mouseY - 2) / this.cellSize),
      col: Math.floor((mouseX - 2 - this.padLeft) / this.cellSize)
    };
  }

  private drawDayCell(row: number, col: number, isSelected: boolean): void {
    const cellSize = this.cellSize;
    const idx = row * this.columnCount + col;
    if (idx >= this.totalCells)  return;

    const x = col * cellSize;
    const y = row * cellSize;
    const cellDate = this.startDate.add(idx, 'day');

    // Check for events on this day
    const event = this.dataManager.getEventForDay(idx);
    let color: Color;
    
    if (event) {
      // Use the event's color
      color = parseColor(event.color);
    } else {
      color = this.defaultColor;
    }
    if (!cellDate.isBefore(this.nowDate, 'day')) {
      color = mixColors(color, this.backgroundColor, 0.75);
    }
    if (isSelected) {
      color = mixColors(color, this.highlightColor, 0.5);
    }
    this.ctx.fillStyle = colorToString(color);
    this.ctx.fillRect(x + this.padLeft, y, cellSize, cellSize);
  }

  private drawDayCellByIndex(index: number, isSelected: boolean): void {
    const row = Math.floor(index / this.columnCount);
    const col = index % this.columnCount;
    this.drawDayCell(row, col, isSelected);
  }

  private updateTooltip(cellIndex: number): void {
    if (cellIndex >= 0 && cellIndex < this.totalCells) {

      const startDate = this.startDate;
      const cellDate = startDate.add(cellIndex, 'day');
      let expectedW = 150, expectedH = 40;

      if (this.selectionStartIndex >= 0 && cellIndex != this.selectionStartIndex) {
        const selectionStartDate = startDate.add(this.selectionStartIndex, 'day')
        const minDate = cellIndex < this.selectionStartIndex ? cellDate : selectionStartDate
        const maxDate = cellIndex >= this.selectionStartIndex ? cellDate : selectionStartDate
        const rangeLength = Math.abs(cellIndex - this.selectionStartIndex)
        const tooltipText = minDate.format('YYYY-MM-DD') + ' … ' + maxDate.format('YYYY-MM-DD') + '\n[' + rangeLength + ' days]';
        this.tooltip.textContent = tooltipText;

        expectedH = 65
      }
      else {
        const ageYears = cellDate.diff(startDate, 'year');
        const startYearDate = startDate.add(ageYears, 'year');
        const ageYearDays = cellDate.diff(startYearDate, 'day');
        const dateStr = cellDate.format('YYYY-MM-DD');
        let tooltipText = `${dateStr}\n${ageYears} years ${ageYearDays} days`;
        const event = this.dataManager.getEventForDay(cellIndex);
        if (event && event.description) tooltipText += `\n${event.description}`;
        this.tooltip.textContent = tooltipText;

        expectedH = event && event.description ? 90 : 65
      }

      // position tooltip
      const W = this.canvas.width;
      const H = this.canvas.height;
      const dx = 20, dy = 20;
      if (this.lastMouseX < W - dx - expectedW) {
        this.tooltip.style.left = (this.lastMouseX + dx) + 'px';
        this.tooltip.style.right = '';
      } else {
        this.tooltip.style.right = (W - this.lastMouseX + dx) + 'px';
        this.tooltip.style.left = '';
      }
      if (this.lastMouseY < H - dy - expectedH) {
        this.tooltip.style.top = (this.lastMouseY + dy) + 'px';
        this.tooltip.style.bottom = '';
      } else {
        this.tooltip.style.bottom = (H - this.lastMouseY + dy) + 'px';
        this.tooltip.style.top = '';
      }
      this.tooltip.style.display = 'block';
    } else {
      this.tooltip.style.display = 'none';
    }
  }

  getSelection(): [number, number] {
    let index = this.highlightedCellIndex;
    let length = 1;
    if (index < 0) return [-1, 0];
    if (this.selectionStartIndex >= 0) {
      const end = Math.max(index, this.selectionStartIndex);
      index = Math.min(index, this.selectionStartIndex);
      length = end - index + 1;
    }
    
    return [index, length];
  }
  
  highlightCell(mouseX: number, mouseY: number): void {
    this.lastMouseX = mouseX;
    this.lastMouseY = mouseY;

    const { row, col } = this.mouseToCellCoords(mouseX, mouseY);
    const cellIndex = row * this.columnCount + col;

    if (this.selectionStartIndex >= 0) {
      const oldCellIndex = this.highlightedCellIndex;
      let oldStartIndex = Math.min(this.selectionStartIndex, oldCellIndex);
      let oldEndIndex = Math.max(this.selectionStartIndex, oldCellIndex);
      let newStartIndex = Math.min(this.selectionStartIndex, cellIndex);
      let newEndIndex = Math.max(this.selectionStartIndex, cellIndex);

      this.highlightedCellIndex = cellIndex;

      if (oldCellIndex < 0 && cellIndex < 0) {
        return;
      }
      else if (oldCellIndex < 0) {
        for (let i = newStartIndex; i <= newEndIndex; i++) {
          this.drawDayCellByIndex(i, true);
        }
      }
      else if (cellIndex < 0) {
        for (let i = oldStartIndex; i <= oldEndIndex; i++) {
          this.drawDayCellByIndex(i, false);
        }
      }
      else {
        while (newStartIndex < oldStartIndex) {
          this.drawDayCellByIndex(newStartIndex, true);
          newStartIndex++;
        }
        while (newStartIndex > oldStartIndex) {
          this.drawDayCellByIndex(oldStartIndex, false);
          oldStartIndex++;
        }
        while (newEndIndex > oldEndIndex) {
          this.drawDayCellByIndex(newEndIndex, true);
          newEndIndex--;
        }
        while (newEndIndex < oldEndIndex) {
          this.drawDayCellByIndex(oldEndIndex, false);
          oldEndIndex--;
        }
      }

      this.updateTooltip(cellIndex);
    }
    else {
      if (this.highlightedCellIndex >= 0) {
        this.drawDayCellByIndex(this.highlightedCellIndex, false);
      }
  
      if (col >= 0 && col < this.columnCount && row >= 0 && row < this.rowCount) {
        this.highlightedCellIndex = cellIndex;
        this.drawDayCellByIndex(cellIndex, true);
        this.updateTooltip(cellIndex);
      } else {
        this.highlightedCellIndex = -1;
        this.updateTooltip(-1);
      }
    }
  }

  startSelection(mouseX: number, mouseY: number): void {
    this.lastMouseX = mouseX;
    this.lastMouseY = mouseY;

    const { row, col } = this.mouseToCellCoords(mouseX, mouseY);
    if (col >= 0 && col < this.columnCount && row >= 0 && row < this.rowCount) {
      const cellIndex = row * this.columnCount + col;
      this.selectionStartIndex = cellIndex;
      this.highlightedCellIndex = cellIndex;
    }
  }

  clearSelection(): void {
    this.highlightCell(-1, -1);
    this.selectionStartIndex = -1;
    this.highlightedCellIndex = -1;
  }
} 